<template>
  <div class="chat-container bg-white/90 backdrop-blur shadow-xl border border-slate-200/80 rounded-2xl">
    <div class="chat-header bg-white/80 backdrop-blur-sm">
      <div class="header-row title-row">
        <h1>特教求索(DeepSeek R1)</h1>
      </div>
      <div class="header-row buttons-row">
        <a href="https://tejiao123.com" class="more-tools-button">
          <svg viewBox="0 0 24 24" width="16" height="16">
            <path fill="currentColor" d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
          </svg>
          更多特教ai工具
        </a>
        <button class="clear-button" @click="clearHistory">
          清除聊天记录
        </button>
      </div>
    </div>

    <div class="messages-container bg-gradient-to-b from-slate-50/60 to-white" ref="messagesContainer">
      <div v-if="messages.length === 0" class="welcome-container">
        <div class="welcome-message">
          你好呀，我是七维教育部署的AI助手特教求索，接入了DeepSeek R1大模型，有什么问题尽管问我吧。
        </div>
        <div class="example-questions">
          <div class="question-title">你可以试着问我：</div>
          <div class="questions-list">
            <div 
              v-for="question in exampleQuestions" 
              :key="question"
              class="example-question"
              @click="selectQuestion(question)"
            >
              {{ question }}
            </div>
          </div>
        </div>
      </div>
      <div v-for="message in messages" :key="message.id" class="message-wrapper">
        <ChatMessage 
          :message="message" 
          :thinking-time="isThinking && message === messages[messages.length - 1] ? thinkingTime : 0"
          :is-streaming="isStreaming && message === messages[messages.length - 1]"
        />
      </div>
    </div>

    <div class="input-container bg-white/90 backdrop-blur-sm">
      <FileUploader 
        ref="fileUploaderRef"
        @update:fileContexts="updateFileContexts" 
      />
      <div class="input-row">
        <div class="textarea-wrapper">
          <textarea
            v-model="userInput"
            @keydown.enter.prevent="handleEnter"
            @input="autoResize"
            ref="inputTextarea"
            placeholder="输入消息..."
            :disabled="isStreaming"
          ></textarea>
          <div class="textarea-sizer" ref="textareaSizer">{{ userInput + '\n' }}</div>
        </div>
        <button 
          :class="['send-button', { 'stop-button': isStreaming }]"
          @click="isStreaming ? stopResponse() : sendMessage()"
          :disabled="!userInput.trim() && !isStreaming"
        >
          {{ isStreaming ? '停止' : '发送' }}
        </button>
      </div>
    </div>

    <Toast ref="toast" />
    
    <div v-if="showClearConfirm" class="confirm-dialog">
      <div class="confirm-content">
        <h3>确认清除</h3>
        <p>确定要清除所有聊天记录吗？此操作不可恢复。</p>
        <div class="confirm-buttons">
          <button @click="showClearConfirm = false">取消</button>
          <button class="danger" @click="confirmClear">确定清除</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick, watch, onUnmounted } from 'vue';
import { apiService, logChat } from '../utils/api';
import ChatMessage from './ChatMessage.vue';
import '../styles/highlight.scss';
import Toast from './Toast.vue';
import FileUploader from './FileUploader.vue';

const userInput = ref('');
const messages = ref([]);
const isThinking = ref(false);
const thinkingTime = ref(0);
const finalThinkingTime = ref(0);
const messagesContainer = ref(null);
let thinkingTimer = null;
const toast = ref(null);
const showClearConfirm = ref(false);
const inputTextarea = ref(null);
const textareaSizer = ref(null);
const currentStream = ref(null);
const shouldAutoScroll = ref(true);
const isStreaming = ref(false);
const maxRetries = 3;
const fileContexts = ref([]); // 存储解析后的文件内容
const fileUploaderRef = ref(null);
const CONTEXT_RETENTION_DAYS = 3;
const CONTEXT_RETENTION_MS = CONTEXT_RETENTION_DAYS * 24 * 60 * 60 * 1000;

const exampleQuestions = [
  "我班上有个中度自闭症的学生，请问在生活课文的课堂教学中，可以给他提供怎样的支持",
  "我想申请今年的特殊教育示范校，你能给我提供一些课题建议吗？",
  "自闭症日马上了，你能帮我设计一个自闭症日的活动方案吗？"
];

const selectQuestion = (question) => {
  userInput.value = question;
  nextTick(() => {
    autoResize();
    inputTextarea.value?.focus();
  });
};

const clearHistory = () => {
  showClearConfirm.value = true;
};

const confirmClear = () => {
  messages.value = [];
  localStorage.removeItem('chatHistory');
  showClearConfirm.value = false;
  
  // 清除文件上传状态
  if (fileUploaderRef.value && fileUploaderRef.value.fileContexts) {
    fileUploaderRef.value.fileContexts.value = [];
  }
  fileContexts.value = []; // 清除本地保存的文件上下文
  
  toast.value?.showToast({
    message: '聊天记录已清除',
    type: 'success'
  });
};

const startThinking = () => {
  isThinking.value = true;
  thinkingTime.value = 0;
  finalThinkingTime.value = 0;
  thinkingTimer = setInterval(() => {
    thinkingTime.value++;
  }, 1000);
};

const stopThinkingTimer = () => {
  isThinking.value = false;
  if (thinkingTimer) {
    clearInterval(thinkingTimer);
    thinkingTimer = null;
  }
};

const handleEnter = (e) => {
  if (!e.shiftKey) {
    sendMessage();
  }
};

// 自动调整输入框高度
const autoResize = () => {
  if (!inputTextarea.value || !textareaSizer.value) return;
  
  // 更新sizer内容
  textareaSizer.value.textContent = userInput.value + '\n';
  
  // 获取计算后的高度
  const newHeight = Math.min(
    Math.max(44, textareaSizer.value.scrollHeight),
    200 // 最大高度
  );
  
  // 设置textarea高度
  inputTextarea.value.style.height = `${newHeight}px`;
};

// 监听输入内容变化
watch(userInput, () => {
  nextTick(autoResize);
});

// 在移动设备上调整视口高度
const adjustViewportHeight = () => {
  const vh = window.innerHeight * 0.01;
  document.documentElement.style.setProperty('--vh', `${vh}px`);
};

// 监听滚动事件
const handleScroll = () => {
  if (!messagesContainer.value) return;
  const { scrollTop, scrollHeight, clientHeight } = messagesContainer.value;
  // 如果用户向上滚动，停止自动滚动
  shouldAutoScroll.value = Math.abs(scrollHeight - scrollTop - clientHeight) < 100;
};

onMounted(() => {
  // 初始化输入框大小
  autoResize();
  // 添加滚动监听
  messagesContainer.value?.addEventListener('scroll', handleScroll);
  // 加载历史记录
  loadHistory();
  
  // 监听窗口大小变化
  window.addEventListener('resize', adjustViewportHeight);
  adjustViewportHeight();
  
  // 初始更新viewport高度
  adjustViewportHeight();
});

onUnmounted(() => {
  window.removeEventListener('resize', adjustViewportHeight);
  // 移除滚动监听
  messagesContainer.value?.removeEventListener('scroll', handleScroll);
});

const loadHistory = () => {
  try {
    const history = localStorage.getItem('chatHistory');
    if (history) {
      messages.value = JSON.parse(history);
      scrollToBottom();
    }
  } catch (error) {
    console.error('Error loading history:', error);
  }
};

const saveHistory = () => {
  try {
    localStorage.setItem('chatHistory', JSON.stringify(messages.value));
  } catch (error) {
    console.error('Error saving history:', error);
  }
};

const scrollToBottom = () => {
  if (!shouldAutoScroll.value) return;
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
    }
  });
};

const processStreamData = async (reader, decoder, retryCount = 0, currentMessage) => {
  try {
    let isFirstChunk = true;
    let isReasoningComplete = false;
    
    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        stopThinkingTimer();
        break;
      }
      
      const chunk = decoder.decode(value);
      const lines = chunk.split('\n');
      
      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            // 忽略 [DONE] 消息
            if (line.includes('[DONE]')) continue;
            
            const data = JSON.parse(line.slice(5));
            // 支持新API返回格式：可能在delta或message中
            const delta = data.choices[0].delta || data.choices[0].message;
            
            // 新API使用reasoning_content字段，对应于本应用中的reasoning字段
            if (delta.reasoning_content && delta.reasoning_content !== 'null') {
              if (isFirstChunk) {
                isFirstChunk = false;
                finalThinkingTime.value = thinkingTime.value;
                stopThinkingTimer();
              }
              currentMessage.reasoning += delta.reasoning_content;
            } 
            
            // content字段保持不变
            if (delta.content && delta.content !== 'null') {
              if (!isReasoningComplete) {
                isReasoningComplete = true;
                await new Promise(resolve => setTimeout(resolve, 500));
              }
              currentMessage.content += delta.content;
            }
            
            const index = messages.value.findIndex(m => m.id === currentMessage.id);
            if (index !== -1) {
              messages.value[index] = { 
                ...currentMessage,
                thinkingTime: finalThinkingTime.value
              };
              scrollToBottom();
            }
          } catch (e) {
            // 静默处理解析错误
            console.error('Error parsing stream data:', e);
          }
        }
      }
    }
    
    saveHistory();
    
  } catch (error) {
    if (error.name === 'AbortError') {
      console.log('Stream aborted');
      isStreaming.value = false;
      currentStream.value = null;
      stopThinkingTimer();
      const index = messages.value.findIndex(m => m.id === currentMessage.id);
      if (index !== -1) {
        messages.value[index] = { 
          ...currentMessage,
          thinkingTime: finalThinkingTime.value
        };
        saveHistory();
      }
    } else if (retryCount < maxRetries) {
      console.log(`Retrying... (${retryCount + 1}/${maxRetries})`);
      await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
      return processStreamData(reader, decoder, retryCount + 1, currentMessage);
    } else {
      console.error('Stream error:', error);
      handleError(error);
    }
  }
};

const getMessageTimestamp = (msg) => {
  if (!msg) return 0;
  return msg.timestamp || msg.id || 0;
};

const sendMessage = async () => {
  if (!userInput.value.trim()) return;
  
  const question = userInput.value;
  shouldAutoScroll.value = true;
  const userMessage = {
    id: Date.now(),
    type: 'user',
    content: question,
    timestamp: Date.now()
  };
  
  const aiMessage = {
    id: Date.now() + 1,
    type: 'ai',
    reasoning: '',
    content: '',
    timestamp: Date.now()
  };
  
  messages.value.push(userMessage);
  messages.value.push(aiMessage);
  saveHistory();
  
  userInput.value = '';
  isStreaming.value = true;
  startThinking();
  scrollToBottom();
  
  try {
    // 获取文件上下文
    let filesContent = '';
    if (fileContexts.value.length > 0) {
      // 只获取状态为"done"的文件
      const seen = new Set();
      const processedFiles = fileContexts.value
        .filter(f => f.status === 'done')
        .filter(f => {
          const key = `${f.filename}::${f.size || 0}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
      console.log('文件上下文:', processedFiles);
      
      if (processedFiles.length > 0) {
        filesContent = processedFiles.map(f => 
          `[文件: ${f.filename}]\n${f.content.substring(0, 10000)}` // 限制每个文件内容长度
        ).join('\n\n');
        console.log('添加的文件内容长度:', filesContent.length);
      }
    }
    
    // 构建消息数组，添加文件内容作为上下文
    const contextMessages = [];
    const cutoff = Date.now() - CONTEXT_RETENTION_MS;
    const recentMessages = messages.value
      .filter(msg => getMessageTimestamp(msg) >= cutoff);
    
    // 添加系统提示
    contextMessages.push({
      role: 'system',
      content: '你是一名专业的特殊教育从业者，有扎实的特殊教育理论知识，并且在中国有丰富的特殊教育实践经验。关于特殊教育，有些问题希望听听你的建议。'
    });
    
    // 如果有文件上下文，添加到系统消息中
    if (filesContent && filesContent.trim() !== '') {
      contextMessages.push({
        role: 'system', 
        content: `以下是用户上传的文件内容，请在回答用户问题时参考这些内容：\n\n${filesContent}`
      });
      console.log('已添加文件上下文到系统消息');
    }
    
    // 添加对话历史
    contextMessages.push(...recentMessages
      .filter(msg => msg.type !== 'ai' || msg.content.trim() !== '')
      .map(msg => ({
        role: msg.type === 'user' ? 'user' : 'assistant',
        content: msg.content
      }))
    );
    
    const { stream, stop } = await apiService.streamChat(contextMessages);
    
    currentStream.value = { stop };
    const decoder = new TextDecoder();
    await processStreamData(stream, decoder, 0, aiMessage);
  } catch (error) {
    handleError(error);
  } finally {
    isStreaming.value = false;
    currentStream.value = null;
    
    // 在对话完成后静默记录日志
    try {
      await logChat(question, {
        reasoning: aiMessage.reasoning,
        reasoning_content: aiMessage.reasoning,
        content: aiMessage.content,
        thinking_time: finalThinkingTime.value
      });
    } catch (e) {
      // 静默处理日志错误
      console.error('Logging error:', e);
    }
  }
};

const stopResponse = () => {
  if (currentStream.value) {
    currentStream.value.stop();
    currentStream.value = null;
    isStreaming.value = false;
    stopThinkingTimer();
    toast.value?.showToast({
      message: '已停止响应',
      type: 'info'
    });
  }
};

const handleError = (error) => {
  console.error('Chat error:', error);
  messages.value.push({
    id: Date.now(),
    type: 'ai',
    content: '抱歉，发生了错误，请稍后重试。',
    timestamp: Date.now()
  });
  saveHistory();
  toast.value?.showToast({
    message: '发生错误，请稍后重试',
    type: 'error'
  });
};

const updateFileContexts = (newFileContexts) => {
  // 复制一份，避免父子间共享引用导致旧文件内容残留
  fileContexts.value = (newFileContexts || []).map(f => ({ ...f }));
};
</script>

<style lang="scss" scoped>
@import '../styles/variables.scss';

.chat-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  height: calc(var(--vh, 1vh) * 100); // 使用自定义视口高度
  background-color: white;
  border-radius: $border-radius-lg;
  box-shadow: $shadow-md;
  
  @media (max-width: 768px) {
    border-radius: 0;
    height: 100vh;
    height: calc(var(--vh, 1vh) * 100);
    box-shadow: none;
    margin: 0;
    padding: 0;
  }
}

.chat-header {
  display: flex;
  flex-direction: column;
  padding: $spacing-md $spacing-lg;
  border-bottom: 1px solid $border-color;
  background-color: #ffffff;
  transition: box-shadow 0.3s ease;
  
  @media (max-width: 768px) {
    padding: $spacing-sm $spacing-md $spacing-md;
    position: sticky;
    top: 0;
    z-index: 10;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  }
  
  @media (min-width: 769px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: $spacing-md $spacing-lg;
    position: relative;
  }
  
  .header-row {
    display: flex;
    width: 100%;
    
    &.title-row {
      justify-content: center;
      margin-bottom: $spacing-sm;
      
      @media (max-width: 768px) {
        padding-bottom: 6px;
        margin-bottom: 6px;
      }
      
      @media (min-width: 769px) {
        margin-bottom: 0;
        width: 100%;
        position: absolute;
        left: 0;
        justify-content: center;
      }
    }
    
    &.buttons-row {
      justify-content: space-between;
      
      @media (min-width: 769px) {
        width: 100%;
        justify-content: space-between;
        position: relative;
        z-index: 2;
      }
    }
  }

  h1 {
    font-size: 1.5rem;
    font-weight: 600;
    text-align: center;
    margin: 0;
    color: $primary-color;
    
    @media (max-width: 768px) {
      font-size: 1.35rem;
      margin: 2px 0;
      letter-spacing: -0.02em;
    }
    
    @media (min-width: 769px) {
      position: relative;
      z-index: 1;
    }
  }

  .clear-button {
    padding: $spacing-sm $spacing-md;
    border-radius: $border-radius-sm;
    border: none;
    background-color: $background-color;
    color: $text-color;
    cursor: pointer;
    transition: all 0.2s ease;
    font-weight: 500;

    &:hover {
      background-color: darken($background-color, 5%);
    }
    
    &:active {
      transform: scale(0.96);
    }
    
    @media (max-width: 768px) {
      padding: 8px $spacing-md;
      font-size: 0.9rem;
      flex: 1;
      max-width: 48%;
      border-radius: $border-radius-md;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    }
  }
  
  .more-tools-button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: $spacing-sm $spacing-md;
    border-radius: $border-radius-sm;
    background-color: $background-color;
    color: $primary-color;
    text-decoration: none;
    font-size: 0.9rem;
    transition: all 0.2s ease;
    font-weight: 500;
    
    svg {
      margin-right: 4px;
      
      @media (max-width: 768px) {
        width: 14px;
        height: 14px;
      }
    }
    
    &:hover {
      background-color: rgba($primary-color, 0.1);
    }
    
    &:active {
      transform: scale(0.96);
    }
    
    @media (max-width: 768px) {
      padding: 8px $spacing-md;
      font-size: 0.9rem;
      flex: 1;
      max-width: 48%;
      border-radius: $border-radius-md;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    }
  }
}

.messages-container {
  flex: 1;
  overflow-y: auto;
  padding: $spacing-lg;
  scroll-behavior: smooth;
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: rgba($border-color, 0.5);
    border-radius: 4px;
    
    &:hover {
      background-color: rgba($border-color, 0.8);
    }
  }
  
  @media (max-width: 768px) {
    padding: $spacing-xs $spacing-sm;
  }
}


.input-container {
  padding: $spacing-md;
  border-top: 1px solid $border-color;
  display: flex;
  flex-direction: column;
  gap: $spacing-sm;
  background-color: white;
  position: sticky;
  bottom: 0;
  
  @media (max-width: 768px) {
    padding: $spacing-xs $spacing-md $spacing-lg;
    gap: $spacing-xs;
    margin-bottom: env(safe-area-inset-bottom, 0);
  }
  
  .input-row {
    display: flex;
    align-items: center;
    gap: $spacing-md;
    
    @media (max-width: 768px) {
      gap: $spacing-sm;
    }
  }
  
  .textarea-wrapper {
    flex: 1;
    position: relative;
    height: 44px;
    
    @media (max-width: 768px) {
      height: 36px;
    }
  }
  
  textarea {
    width: 100%;
    box-sizing: border-box;
    /* 分别控制上下内边距以精确对齐 */
    padding: 10px $spacing-sm 10px;
    margin: 0;
    border: 1px solid $border-color;
    border-radius: $border-radius-lg;
    resize: none;
    font-family: inherit;
    font-size: 0.95rem;
    overflow-y: hidden;
    height: 100%;
    vertical-align: middle;
    
    @media (max-width: 768px) {
      /* 移动端需要调整padding-top更小些，使文本垂直居中 */
      padding: 6px $spacing-sm 10px;
    }
    
    &:focus {
      outline: none;
      border-color: $primary-color;
      box-shadow: 0 0 0 2px rgba($primary-color, 0.1);
    }
  }
  
  .textarea-sizer {
    visibility: hidden;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    padding: 10px $spacing-sm 10px;
    border: 1px solid transparent;
    font-family: inherit;
    font-size: 0.95rem;
    white-space: pre-wrap;
    word-wrap: break-word;
    
    @media (max-width: 768px) {
      padding: 6px $spacing-sm 10px;
    }
  }
  
  .send-button {
    height: 44px;
    margin-left: $spacing-md;
    padding: 0 $spacing-lg;
    border-radius: $border-radius-lg;
    font-weight: 500;
    transition: all 0.2s ease;
    border: none;
    background: linear-gradient(135deg, lighten($primary-color, 5%), $primary-color);
    color: white;
    box-shadow: 0 4px 14px rgba($primary-color, 0.18);
    
    &:not(:disabled):hover {
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    &:disabled {
      background: #e2e8f0;
      color: #94a3b8;
      box-shadow: none;
      cursor: not-allowed;
    }

    &.stop-button {
      background: linear-gradient(135deg, #ef4444, #dc2626);
      box-shadow: 0 4px 14px rgba(239, 68, 68, 0.25);
    }
    
    @media (max-width: 768px) {
      height: 36px;
      margin-left: 0;
      padding: 0 $spacing-md;
      font-size: 0.95rem;
    }
  }
}

.thinking-indicator {
  display: inline-block;
  padding: $spacing-sm $spacing-md;
  background-color: $thinking-bg;
  color: $thinking-text;
  border-radius: $border-radius-lg;
  margin: $spacing-xs 0;
  font-size: 0.9rem;
}

.confirm-dialog {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(#000, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);

  .confirm-content {
    background-color: white;
    padding: $spacing-lg;
    border-radius: $border-radius-lg;
    max-width: 400px;
    width: 90%;
    box-shadow: $shadow-md;

    h3 {
      margin-bottom: $spacing-md;
      font-size: 1.2rem;
    }

    p {
      margin-bottom: $spacing-lg;
      color: rgba($text-color, 0.8);
    }

    .confirm-buttons {
      display: flex;
      justify-content: flex-end;
      gap: $spacing-md;

      button {
        padding: $spacing-sm $spacing-lg;
        border-radius: $border-radius-sm;
        border: none;
        cursor: pointer;
        font-size: 0.9rem;
        transition: all 0.2s;

        &:not(.danger) {
          background-color: $background-color;
          color: $text-color;

          &:hover {
            background-color: darken($background-color, 5%);
          }
        }

        &.danger {
          background-color: #FF3B30;
          color: white;

          &:hover {
            background-color: darken(#FF3B30, 5%);
          }
        }
      }
    }
  }
}

.welcome-container {
  padding: $spacing-lg;
  text-align: left;
  max-width: 800px;
  margin: 0 auto;
  background-color: #f8f9fa;
  border-radius: $border-radius-lg;
  
  @media (max-width: 768px) {
    padding: $spacing-sm $spacing-md;
    margin: $spacing-xs $spacing-xs;
    border-radius: $border-radius-md;
  }
}

.welcome-message {
  font-size: 1.15rem;
  color: #333;
  margin-bottom: $spacing-xl;
  line-height: 1.6;
  background-color: transparent;
  padding: 0;
  border-radius: 0;
  border: none;
  font-weight: 500;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: $spacing-md;
    line-height: 1.5;
  }
}

.example-questions {
  .question-title {
    color: #555;
    margin-bottom: $spacing-md;
    font-size: 1rem;
    font-weight: normal;
    
    @media (max-width: 768px) {
      margin-bottom: $spacing-sm;
      font-size: 0.95rem;
    }
  }
}

.questions-list {
  display: flex;
  flex-direction: column;
  gap: $spacing-md;
  
  @media (max-width: 768px) {
    gap: $spacing-sm;
  }
}

.example-question {
  background-color: #f0f7ff;
  color: #2b5fb4;
  padding: $spacing-md $spacing-lg;
  border-radius: $border-radius-md;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.95rem;
  border: 1px solid rgba(#2b5fb4, 0.1);
  
  &:hover {
    background-color: #e6f0ff;
    border-color: rgba(#2b5fb4, 0.2);
    transform: translateY(-1px);
  }
  
  @media (max-width: 768px) {
    padding: $spacing-sm $spacing-md;
    font-size: 0.9rem;
    border-radius: $border-radius-sm;
  }
}
</style> 
